'use client'

import { forwardRef, type ReactNode, type ComponentPropsWithoutRef } from 'react'
import { Lock } from '@/components/ui/icons'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip'
import { useRealAccountCommandGate } from './useRealAccountCommandGate'

interface GatedButtonProps extends Omit<ComponentPropsWithoutRef<typeof Button>, 'onClick'> {
  /** The action to perform when the user has a real account */
  onClick?: () => void
  /** Project ID for reconnecting provider after sign-up */
  projectId?: string
  /** Custom return path after OAuth */
  returnPath?: string
  /** Tooltip text shown when hovering over the lock icon */
  gatedTooltip?: string
  /** Whether to show the lock icon for anonymous users */
  showLockIcon?: boolean
  /** Children to render inside the button */
  children: ReactNode
}

/**
 * A button that gates actions behind account creation.
 *
 * For anonymous users:
 * - Shows a lock icon (optional)
 * - Opens sign-up modal on click
 * - Does not replay the original action after successful sign-up
 *
 * For real accounts:
 * - Works as a normal button
 */
export const GatedButton = forwardRef<HTMLButtonElement, GatedButtonProps>(
  (
    {
      onClick,
      projectId,
      returnPath,
      gatedTooltip = 'Sign up required',
      showLockIcon = true,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    const { isGated, guardAction, SignUpModalComponent } = useRealAccountCommandGate({ projectId, returnPath })

    const handleClick = () => {
      if (!guardAction('gated-button')) return
      onClick?.()
    }

    // If user has a real account, render a normal button
    if (!isGated) {
      return (
        <Button ref={ref} onClick={onClick} disabled={disabled} {...props}>
          {children}
        </Button>
      )
    }

    // For anonymous users, show gated button with lock icon
    return (
      <>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button ref={ref} onClick={handleClick} disabled={disabled} {...props}>
                {showLockIcon && <Lock className="w-3.5 h-3.5 me-1.5 opacity-70" />}
                {children}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="text-xs">{gatedTooltip}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {SignUpModalComponent}
      </>
    )
  },
)

GatedButton.displayName = 'GatedButton'

/**
 * Hook to check if a feature is gated and handle the sign-up flow.
 *
 * Usage:
 * ```tsx
 * const { isGated, showSignUp, SignUpModalComponent } = useGatedFeature({ projectId })
 *
 * const handleUpload = () => {
 *   if (isGated) {
 *     showSignUp()
 *     return
 *   }
 *   // ... perform upload
 * }
 * ```
 *
 * Signup success never replays a previously supplied callback. The user must
 * trigger the command again after creating an account.
 */
export function useGatedFeature(options?: { projectId?: string; returnPath?: string }) {
  const { isGated, guardAction, SignUpModalComponent } = useRealAccountCommandGate(options)

  const showSignUp = (actionNameOrPreviousCallback?: string | (() => void)) => {
    guardAction(typeof actionNameOrPreviousCallback === 'string' ? actionNameOrPreviousCallback : 'gated-feature')
  }

  return {
    isGated,
    guardAction,
    showSignUp,
    SignUpModalComponent,
  }
}
